import { useEffect, useState, useRef } from "react";
import { Plus, Edit, Trash2, AlertCircle, Loader2 } from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

const emptyForm = { title: "", slug: "", content: "", status: "Active" };

export default function Pages() {
    const [pages, setPages] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const formRef = useRef(null);
    const titleInputRef = useRef(null);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Multi-delete state
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchPages = async () => {
        try {
            setLoading(true);
            setError("");
            const { data } = await API.get("/admin/pages", { params: { page, limit, search } });
            if (!data?.success) throw new Error(data?.message || "Failed to load pages");
            setPages(Array.isArray(data.pages) ? data.pages : []);
            setTotal(data.total || 0);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Failed to load pages");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, search]);

    const handleToggleSelectAll = () => {
        if (selectedIds.length === pages.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(pages.map((p) => p._id));
        }
    };

    const handleToggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleBatchDelete = async () => {
        try {
            setIsBatchDeleting(true);
            const { data } = await API.post("/admin/batch-delete", {
                entity: "pages",
                ids: selectedIds,
            });
            if (data?.success) {
                setSelectedIds([]);
                fetchPages();
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Batch delete failed");
        } finally {
            setIsBatchDeleting(false);
        }
    };

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError("");
            const payload = { ...form };
            if (!payload.slug) delete payload.slug;
            if (editingId) {
                await API.put(`/admin/pages/${editingId}`, payload);
            } else {
                await API.post("/admin/pages", payload);
            }
            resetForm();
            fetchPages();
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (pageItem) => {
        setEditingId(pageItem._id);
        setForm({
            title: pageItem.title || "",
            slug: pageItem.slug || "",
            content: pageItem.content || "",
            status: pageItem.status || "Active",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
            titleInputRef.current?.focus({ preventScroll: true });
        }, 150);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this page?")) return;
        try {
            await API.delete(`/admin/pages/${id}`);
            setPages((prev) => prev.filter((p) => p._id !== id));
            setSelectedIds((prev) => prev.filter((i) => i !== id));
            setTotal((t) => Math.max(t - 1, 0));
        } catch (err) {
            setError(err?.response?.data?.message || "Delete failed");
        }
    };

    const totalPages = Math.max(Math.ceil(total / limit), 1);

    return (
        <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Manage Pages</h1>
                    <p className="text-sm text-gray-500">Create and edit custom pages</p>
                </div>
            </div>

            {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                </div>
            )}

            <form
                ref={formRef}
                onSubmit={handleSubmit}
                className={`bg-white dark:bg-slate-800 rounded-xl p-6 shadow-lg border transition-all duration-300 space-y-4 scroll-mt-24 ${
                    editingId
                        ? "border-pink-500 ring-2 ring-pink-500/20 shadow-pink-500/10"
                        : "border-gray-200 dark:border-slate-700"
                }`}
            >
                {editingId && (
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-slate-700/60">
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                            <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                                Editing Page: {form.title || "Untitled"}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
                        >
                            Cancel Edit
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Title
                        <input
                            ref={titleInputRef}
                            required
                            type="text"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </label>
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Slug (optional)
                        <input
                            type="text"
                            value={form.slug}
                            onChange={(e) => setForm({ ...form, slug: e.target.value })}
                            placeholder="auto-generated from title"
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </label>
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Status
                        <select
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        >
                            <option value="Active">Active</option>
                            <option value="Draft">Draft</option>
                        </select>
                    </label>
                </div>
                <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200 block">
                    Content
                    <textarea
                        required
                        rows={6}
                        value={form.content}
                        onChange={(e) => setForm({ ...form, content: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                </label>
                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-60 cursor-pointer"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {editingId ? "Update Page" : "Create Page"}
                    </button>
                    {editingId && (
                        <button type="button" onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer">Cancel edit</button>
                    )}
                </div>
            </form>

            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-slate-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-b border-gray-200 dark:border-slate-700">
                    <div className="text-sm text-gray-600">Total: {total}</div>
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => { setPage(1); setSearchInput(e.target.value); }}
                        placeholder="Search title or slug"
                        className="px-4 py-2 bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 w-56"
                    />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-slate-700/50">
                                <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                                    <input
                                        type="checkbox"
                                        checked={pages.length > 0 && selectedIds.length === pages.length}
                                        onChange={handleToggleSelectAll}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                    />
                                </th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Title</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Slug</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Updated</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                            {loading ? (
                                <tr><td colSpan={6} className="py-8 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading...</td></tr>
                            ) : pages.length === 0 ? (
                                <tr><td colSpan={6} className="py-6 text-center text-gray-500">No pages yet</td></tr>
                            ) : (
                                pages.map((pageItem) => {
                                    const isSelected = selectedIds.includes(pageItem._id);
                                    return (
                                    <tr key={pageItem._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                                        <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleSelectRow(pageItem._id)}
                                                className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-800 dark:text-gray-200 font-medium">
                                            <div className="flex flex-col">
                                                <span>{pageItem.title}</span>
                                                <span className="text-xs text-gray-500 line-clamp-1">{pageItem.content}</span>
                                            </div>
                                        </td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-pink-500">{pageItem.slug}</td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-500">{pageItem.updatedAt ? new Date(pageItem.updatedAt).toLocaleString() : "-"}</td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${pageItem.status === "Active"
                                                ? "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400"
                                                : "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400"}`}>
                                                {pageItem.status}
                                            </span>
                                        </td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 space-x-2">
                                            <button onClick={() => handleEdit(pageItem)} className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                                                <Edit className="w-3.5 h-3.5" /> Edit
                                            </button>
                                            <button onClick={() => handleDelete(pageItem._id)} className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300">
                                                <Trash2 className="w-3.5 h-3.5" /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-t border-gray-200 dark:border-slate-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Page {page} of {totalPages}</p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage((p) => Math.max(p - 1, 1))}
                            disabled={page === 1 || loading}
                            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                            disabled={page >= totalPages || loading}
                            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {/* Floating Multi-Delete Bar */}
            <BatchActionBar
                selectedCount={selectedIds.length}
                totalCount={total}
                onClearSelection={() => setSelectedIds([])}
                onSelectAll={handleToggleSelectAll}
                onDeleteSelected={handleBatchDelete}
                isDeleting={isBatchDeleting}
                entityName="pages"
            />
        </div>
    );
}
